import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import * as ct from "countries-and-timezones";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import {
  getGroupedTimezoneSelectOptions,
  getUtcOffsetLabel,
} from "../utils/timezoneOptions";

const LIST_MAX_HEIGHT = 280;
const GROUP_PREVIEW_LIMIT = 3;

/**
 * Common city aliases users search for, mapped to canonical IANA zones.
 * This helps queries like "delhi" resolve to Asia/Kolkata.
 */
const SEARCH_ALIASES_BY_TIMEZONE = {
  "Asia/Kolkata": ["delhi", "new delhi", "mumbai", "bombay", "bangalore", "bengaluru", "chennai", "kolkata", "calcutta", "hyderabad", "pune"],
  "Asia/Calcutta": ["delhi", "new delhi", "mumbai", "bombay", "bangalore", "bengaluru", "chennai", "kolkata", "calcutta", "hyderabad", "pune"],
  "America/New_York": ["new york", "nyc", "manhattan", "brooklyn", "queens", "bronx"],
  "America/Los_Angeles": ["los angeles", "la", "hollywood", "san diego", "san francisco", "sf", "silicon valley"],
  "America/Chicago": ["chicago", "houston", "dallas", "austin", "minneapolis"],
  "America/Denver": ["denver", "phoenix", "salt lake city", "albuquerque"],
  "America/Toronto": ["toronto", "montreal", "ottawa", "vancouver", "calgary"],
  "America/Sao_Paulo": ["sao paulo", "sao-paulo", "rio", "rio de janeiro", "brasilia"],
  "America/Mexico_City": ["mexico city", "cdmx", "guadalajara", "monterrey"],
  "Pacific/Honolulu": ["honolulu", "hawaii"],
  "America/Anchorage": ["anchorage", "alaska"],
  "Europe/London": ["london", "uk", "england"],
  "Europe/Paris": ["paris"],
  "Europe/Berlin": ["berlin", "munich", "frankfurt"],
  "Europe/Madrid": ["madrid", "barcelona", "spain"],
  "Europe/Rome": ["rome", "milan", "italy"],
  "Europe/Amsterdam": ["amsterdam", "netherlands"],
  "Europe/Brussels": ["brussels", "belgium"],
  "Europe/Zurich": ["zurich", "geneva", "switzerland"],
  "Europe/Vienna": ["vienna", "austria"],
  "Europe/Stockholm": ["stockholm", "sweden"],
  "Europe/Oslo": ["oslo", "norway"],
  "Europe/Copenhagen": ["copenhagen", "denmark"],
  "Europe/Warsaw": ["warsaw", "poland"],
  "Europe/Moscow": ["moscow", "saint petersburg", "st petersburg"],
  "Europe/Istanbul": ["istanbul", "ankara", "turkiye", "turkey"],
  "Africa/Cairo": ["cairo", "egypt"],
  "Africa/Johannesburg": ["johannesburg", "cape town", "south africa"],
  "Africa/Nairobi": ["nairobi", "kenya"],
  "Africa/Lagos": ["lagos", "abuja", "nigeria"],
  "Asia/Dubai": ["dubai", "abu dhabi", "uae"],
  "Asia/Riyadh": ["riyadh", "jeddah", "saudi"],
  "Asia/Karachi": ["karachi", "lahore", "islamabad", "pakistan"],
  "Asia/Dhaka": ["dhaka", "bangladesh"],
  "Asia/Kathmandu": ["kathmandu", "nepal"],
  "Asia/Colombo": ["colombo", "sri lanka"],
  "Asia/Bangkok": ["bangkok", "thailand"],
  "Asia/Jakarta": ["jakarta", "indonesia"],
  "Asia/Manila": ["manila", "philippines"],
  "Asia/Ho_Chi_Minh": ["ho chi minh", "saigon", "vietnam"],
  "Asia/Kuala_Lumpur": ["kuala lumpur", "malaysia"],
  "Asia/Singapore": ["singapore"],
  "Asia/Hong_Kong": ["hong kong", "hk"],
  "Asia/Shanghai": ["shanghai", "beijing", "china"],
  "Asia/Taipei": ["taipei", "taiwan"],
  "Asia/Tokyo": ["tokyo", "osaka"],
  "Asia/Seoul": ["seoul", "south korea", "korea"],
  "Asia/Tehran": ["tehran", "iran"],
  "Asia/Jerusalem": ["jerusalem", "tel aviv", "israel"],
  "Australia/Sydney": ["sydney", "melbourne", "brisbane"],
  "Australia/Perth": ["perth", "western australia"],
  "Pacific/Auckland": ["auckland", "wellington", "new zealand"],
};

function normalizeSearchText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getPreviewTextForGroup(options) {
  if (!Array.isArray(options) || options.length === 0) return "";
  const names = options.map((item) =>
    String(item.label || "")
      .replace(/^UTC[+-]\d{2}:\d{2}\s*-\s*/i, "")
      .trim(),
  );
  const preview = names.slice(0, GROUP_PREVIEW_LIMIT).join(", ");
  const extra = names.length - GROUP_PREVIEW_LIMIT;
  return extra > 0 ? `${preview} +${extra} more` : preview;
}

function getGroupNames(options) {
  return (options || []).map((item) =>
    String(item.label || "")
      .replace(/^UTC[+-]\d{2}:\d{2}\s*-\s*/i, "")
      .trim(),
  );
}

function getAliasSearchTextForOption(option) {
  const baseAliases = SEARCH_ALIASES_BY_TIMEZONE[option.value] || [];
  const tzMeta = ct.getTimezone(option.value);
  const canonical = tzMeta?.aliasOf || null;
  const canonicalAliases = canonical
    ? SEARCH_ALIASES_BY_TIMEZONE[canonical] || []
    : [];
  return normalizeSearchText([...baseAliases, ...canonicalAliases].join(" "));
}

function filterOptions(options, query) {
  const q = normalizeSearchText(query);
  if (!q) return options;
  return options.filter((o) => {
    const valueNorm = normalizeSearchText(o.value);
    const labelNorm = normalizeSearchText(o.label);
    const aliasNorm = getAliasSearchTextForOption(o);
    if (valueNorm.includes(q) || labelNorm.includes(q) || aliasNorm.includes(q)) {
      return true;
    }
    return normalizeSearchText(
      o.value
      .split("/")
      .join(" "),
    ).includes(q);
  });
}

function filterGroups(groups, query) {
  const q = normalizeSearchText(query);
  if (!q) return groups;
  return groups
    .map((group) => {
      const filteredOptions = filterOptions(group.options, q);
      if (filteredOptions.length === 0) return null;
      return {
        ...group,
        options: filteredOptions,
        zonesText: filteredOptions.map((item) => item.label).join(" | "),
      };
    })
    .filter(Boolean);
}

/**
 * Searchable combobox for IANA timezones (full list with UTC offset in label).
 * @param {string} value — IANA id
 * @param {(iana: string) => void} onChange
 */
export function SearchableTimezoneSelect({
  value,
  onChange,
  disabled,
  placeholder = "Search or select timezone",
  className,
}) {
  const groupedOptions = useMemo(() => getGroupedTimezoneSelectOptions(), []);
  const options = useMemo(
    () => groupedOptions.flatMap((group) => group.options),
    [groupedOptions],
  );
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedGroupKeys, setExpandedGroupKeys] = useState(() => new Set());
  const searchInputRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const selectedGroup = groupedOptions.find((group) =>
    group.options.some((o) => o.value === value),
  );
  const displayText =
    selectedGroup?.offsetLabel ||
    (selected
      ? getUtcOffsetLabel(selected.value)
      : null) ||
    (value
      ? `${value} (${getUtcOffsetLabel(value)})`.replace(/\s\(\)$/, "")
      : "");

  const filteredGroups = useMemo(
    () => filterGroups(groupedOptions, search),
    [groupedOptions, search],
  );

  useEffect(() => {
    if (open) {
      setSearch("");
      setExpandedGroupKeys(new Set());
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open]);

  const handleSelect = (itemOrGroup) => {
    if (itemOrGroup?.options) {
      const existingInGroup = itemOrGroup.options.find((opt) => opt.value === value);
      const selectedItem = existingInGroup || itemOrGroup.options[0];
      if (selectedItem) onChange(selectedItem.value);
    } else if (itemOrGroup?.value) {
      onChange(itemOrGroup.value);
    }
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    const flattened = filteredGroups.flatMap((group) => group.options);
    if (e.key === "Enter" && flattened.length === 1) {
      e.preventDefault();
      handleSelect(flattened[0]);
    }
  };

  const toggleGroupPreview = (groupKey) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-10 w-full max-w-xl items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-left ring-offset-background",
            "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !displayText && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate pr-2">{displayText || placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] max-w-[min(100vw-2rem,36rem)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-2 border-b">
          <Input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search city, region, or UTC offset…"
            className="h-9"
            autoComplete="off"
          />
        </div>
        <div
          className="overflow-y-auto overscroll-contain py-1"
          style={{ maxHeight: LIST_MAX_HEIGHT }}
        >
          {filteredGroups.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No matches
            </div>
          ) : (
            filteredGroups.map((group) => (
              <button
                key={group.offsetLabel}
                type="button"
                className={cn(
                  "w-full border-b last:border-b-0 px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground",
                  group.options.some((item) => item.value === value) &&
                    "bg-accent text-accent-foreground",
                )}
                onClick={() => handleSelect(group)}
              >
                <div className="text-sm font-medium">
                  {group.offsetLabel} ({group.options.length})
                </div>
                <div className="text-xs text-muted-foreground break-words">
                  {(() => {
                    const names = getGroupNames(group.options);
                    const isExpanded = expandedGroupKeys.has(group.offsetLabel);
                    const preview = names.slice(0, GROUP_PREVIEW_LIMIT).join(", ");
                    const extra = names.length - GROUP_PREVIEW_LIMIT;
                    if (names.length <= GROUP_PREVIEW_LIMIT) return preview;
                    if (isExpanded) return names.join(", ");
                    return (
                      <>
                        {preview}
                        {" "}
                        <span
                          className="underline underline-offset-2 cursor-pointer"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleGroupPreview(group.offsetLabel);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleGroupPreview(group.offsetLabel);
                            }
                          }}
                        >
                          +{extra} more
                        </span>
                      </>
                    );
                  })()}
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableTimezoneSelect;
